import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, ExternalLink } from "lucide-react";
import { KIND_META, REL_META, CONFIDENCE_META } from "./efConsts";

/**
 * Slide-in detail panel — desktop right drawer / mobile bottom sheet.
 * Closes when `entity` and `edge` are both null.
 */
export const DetailPanel = ({ entity = null, edge = null, allNodes = [], onClose }) => {
  const open = !!(entity || edge);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
        data-testid="ef-detail-overlay"
      />
      <aside
        data-testid="ef-detail-panel"
        className="fixed z-50 bg-[#0F0F0F] border-[#D92D20]/40 shadow-2xl flex flex-col
                   inset-x-0 bottom-0 top-[40%] border-t
                   sm:inset-x-auto sm:inset-y-0 sm:right-0 sm:top-0 sm:bottom-0 sm:w-[460px] sm:border-l"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2D2A26]">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // {entity ? "EVIDENCE FILE" : "CONNECTION FILE"}
          </div>
          <button
            onClick={onClose}
            data-testid="ef-detail-close"
            className="w-9 h-9 flex items-center justify-center border border-[#454038] text-[#A39E93] hover:text-[#D92D20] hover:border-[#D92D20]"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {entity && <EntityDetail entity={entity} />}
          {edge && <EdgeDetail edge={edge} allNodes={allNodes} />}
        </div>
      </aside>
    </>
  );
};

const EntityDetail = ({ entity }) => {
  const meta = KIND_META[entity.kind] || KIND_META.reference;
  return (
    <div className="space-y-4" data-testid={`ef-detail-entity-${entity.id}`}>
      <div className="flex items-center gap-2">
        <span style={{ color: meta.color, borderColor: meta.color }}
              className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
          {meta.label}
        </span>
        {entity.is_anchor && (
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] border border-[#D92D20] px-2 py-0.5">
            // ANCHOR
          </span>
        )}
        {entity.demo && (
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">
            // DEMO
          </span>
        )}
      </div>

      {(() => {
        const photo = entity.thumbnail_url || entity.photo_url || entity.image_url;
        return photo ? (
          <div className="border border-[#2D2A26] aspect-[4/5] bg-[#0a0907] overflow-hidden relative">
            <img src={photo} alt={entity.name} className="w-full h-full object-cover filter grayscale-[15%] contrast-110" />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black/60" />
            <div className="absolute bottom-2 left-2 right-2 font-mono text-[9px] uppercase tracking-[0.3em] text-[#E4DFD3]/85">// CASE PHOTO</div>
          </div>
        ) : (
          <div className="border border-dashed border-[#2D2A26] aspect-[4/5] bg-[#0a0907] flex items-center justify-center text-[#454038]">
            <div className="text-center">
              <div className="text-5xl" style={{ color: meta.color }}>{meta.icon}</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.25em] mt-2 text-[#757065]">no photo on file</div>
            </div>
          </div>
        );
      })()}

      <h2 className="font-heading text-2xl text-[#E4DFD3] leading-tight">{entity.name}</h2>

      {entity.occupation_or_role && (
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{entity.occupation_or_role}</div>
      )}

      {entity.connection_summary_to_epstein && (
        <div className="border-l-2 border-[#D92D20] pl-3 py-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20] mb-1">// CONNECTION TO EPSTEIN</div>
          <p className="text-sm text-[#CBB892] leading-relaxed italic">{entity.connection_summary_to_epstein}</p>
        </div>
      )}

      {entity.summary && (
        <p className="text-sm text-[#A39E93] leading-relaxed">{entity.summary}</p>
      )}

      {entity.legal_note && (
        <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 text-[11px] italic text-[#CBB892]" data-testid="ef-detail-legal-note">
          ⚠ {entity.legal_note}
        </div>
      )}

      <div className="border-t border-[#2D2A26] pt-3 space-y-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// META</div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px]">
          <div className="text-[#757065]">kind</div>
          <div className="text-[#E4DFD3]">{entity.kind}</div>
          {entity.category && (<><div className="text-[#757065]">category</div><div className="text-[#E4DFD3]">{entity.category}</div></>)}
          {entity.nationality && (<><div className="text-[#757065]">nationality</div><div className="text-[#E4DFD3]">{entity.nationality}</div></>)}
          {entity.confidence_level && (<><div className="text-[#757065]">confidence</div>
            <div style={{ color: (CONFIDENCE_META[entity.confidence_level] || {}).color }}>
              {(CONFIDENCE_META[entity.confidence_level] || {}).label || entity.confidence_level}
            </div></>)}
          <div className="text-[#757065]">slug</div>
          <div className="text-[#E4DFD3] break-all">{entity.slug}</div>
          <div className="text-[#757065]">sources</div>
          <div className="text-[#E4DFD3]">{(entity.source_refs || []).length}</div>
        </div>
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0907] px-3 py-2 text-[10px] italic text-[#757065]">
        Inclusion does not imply guilt or criminal conduct. Read the linked sources.
      </div>

      <Link
        to={`/epstein-files/entity/${entity.slug}`}
        data-testid={`ef-detail-open-full-${entity.id}`}
        className="flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] text-white px-4 py-3 font-mono text-[10px] uppercase tracking-[0.3em]"
      >
        Open full file <ArrowRight size={12} />
      </Link>
    </div>
  );
};

const EdgeDetail = ({ edge, allNodes }) => {
  const meta = REL_META[edge.type] || REL_META.other_editor_defined;
  const conf = CONFIDENCE_META[edge.confidence] || CONFIDENCE_META.medium;
  const src = allNodes.find((n) => n.id === edge.src_entity_id);
  const dst = allNodes.find((n) => n.id === edge.dst_entity_id);
  return (
    <div className="space-y-4" data-testid={`ef-detail-edge-${edge.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span style={{ color: meta.color, borderColor: meta.color }}
              className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
          {meta.label}
        </span>
        <span style={{ color: conf.color, borderColor: conf.color }}
              className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5">
          CONFIDENCE: {conf.label}
        </span>
        {edge.demo && (
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">
            // DEMO
          </span>
        )}
      </div>

      <div className="border border-[#2D2A26] bg-[#0a0907] p-3 space-y-2">
        <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065]">// LINKED</div>
        {src && (
          <Link to={`/epstein-files/entity/${src.slug}`} className="block hover:text-[#D92D20]">
            <div className="font-heading text-sm text-[#E4DFD3]">{src.name}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{src.kind}</div>
          </Link>
        )}
        <div className="font-mono text-[10px] text-[#D92D20] text-center">↕</div>
        {dst && (
          <Link to={`/epstein-files/entity/${dst.slug}`} className="block hover:text-[#D92D20]">
            <div className="font-heading text-sm text-[#E4DFD3]">{dst.name}</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">{dst.kind}</div>
          </Link>
        )}
      </div>

      {edge.summary && (
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#757065] mb-1">// EDITOR NOTE</div>
          <p className="text-sm text-[#A39E93] leading-relaxed">{edge.summary}</p>
        </div>
      )}

      <div className="border-t border-[#2D2A26] pt-3 grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[10px]">
        <div className="text-[#757065]">date label</div>
        <div className="text-[#E4DFD3]">{edge.date_label || "—"}</div>
        <div className="text-[#757065]">source refs</div>
        <div className="text-[#E4DFD3]">{(edge.source_refs || []).length}</div>
      </div>
    </div>
  );
};

export default DetailPanel;
